<template>
  <div class="min-vh-100 bg-light pb-5">

    <div class="bg-dark text-white px-3 pt-4 pb-5">
      <div class="mx-auto" style="max-width:460px;">
        <div class="small text-white-50">{{ company.name }}</div>
        <h4 class="font-weight-bold mb-0">{{ member.name || 'My Membership' }}</h4>
      </div>
    </div>

    <div class="px-3" style="margin-top:-2.25rem;">
      <div class="mx-auto" style="max-width:460px;">

        <div v-if="loading" class="card border-0 shadow-sm"><div class="card-body text-center py-5">
          <div class="spinner-border text-primary"></div>
        </div></div>

        <div v-else-if="fatalError" class="alert alert-danger">{{ fatalError }}</div>

        <template v-else>

          <!-- Install prompt -->
          <div v-if="showInstall" class="card border-0 shadow-sm mb-3">
            <div class="card-body d-flex align-items-center">
              <i class="bi bi-phone text-primary mr-3" style="font-size:1.75rem;"></i>
              <div class="flex-grow-1">
                <div class="font-weight-bold text-dark">Add to your home screen</div>
                <div v-if="isIos" class="text-secondary small">
                  Tap <i class="bi bi-box-arrow-up"></i> Share, then <strong>Add to Home Screen</strong>.
                </div>
                <div v-else-if="!installEvent" class="text-secondary small">
                  Open your browser menu <i class="bi bi-three-dots-vertical"></i> and tap <strong>Install app</strong> / <strong>Add to Home screen</strong>.
                </div>
                <div v-else class="text-secondary small">Open your pass in one tap, like an app.</div>
              </div>
              <button v-if="installEvent" class="btn btn-primary ml-2" @click="install">Install</button>
              <button class="btn btn-link text-secondary ml-1 px-1" @click="dismissInstall"><i class="bi bi-x-lg"></i></button>
            </div>
          </div>

          <!-- Status + pass -->
          <div class="card border-0 shadow-sm mb-3">
            <div class="card-body text-center p-4">
              <template v-if="sub">
                <span class="badge px-3 py-2 mb-3" :class="subActive ? 'badge-primary' : 'badge-danger'">
                  {{ subActive ? 'Active' : 'Expired' }}
                </span>
                <h5 class="font-weight-bold text-dark mb-1">{{ sub.package }}</h5>
                <div class="text-secondary small mb-3">
                  <template v-if="subActive">
                    Valid until {{ formatDate(sub.end_date) }} ·
                    <strong class="text-dark">{{ sub.days_left }} day{{ sub.days_left === 1 ? '' : 's' }} left</strong>
                  </template>
                  <template v-else>Ended {{ formatDate(sub.end_date) }}</template>
                </div>
              </template>
              <template v-else>
                <h5 class="font-weight-bold text-dark mb-3">No active plan</h5>
              </template>

              <div class="border rounded p-3 d-inline-block bg-white" v-html="passSvg"></div>
              <div class="text-secondary small mt-2">Show this pass at the desk to check in</div>
            </div>
          </div>

          <!-- Renew / join -->
          <div class="card border-0 shadow-sm mb-3">
            <div class="card-body p-4">
              <template v-if="paidMessage">
                <div class="text-center">
                  <i class="bi bi-check-circle-fill text-primary" style="font-size:2.5rem;"></i>
                  <p class="text-dark mt-2 mb-0">{{ paidMessage }}</p>
                </div>
              </template>
              <template v-else-if="sub && sub.renew_price && !choosePlan">
                <div class="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <div class="font-weight-bold text-dark">{{ subActive ? 'Renew early' : 'Renew now' }}</div>
                    <div class="text-secondary small">
                      {{ subActive ? 'Extends from ' + formatDate(sub.end_date) : 'Starts today' }}
                    </div>
                  </div>
                  <div class="h5 font-weight-bold text-dark mb-0">{{ money(sub.renew_price) }}</div>
                </div>
                <member-pay-panel
                  :member-token="token"
                  :intent="{ type: 'renew', subscription_id: sub.id }"
                  :amount="sub.renew_price"
                  :wallet="member.wallet || 0"
                  :can-pay-online="canPayOnline"
                  :default-phone="member.phone || ''"
                  @done="afterPayment"
                />
                <button v-if="packages.length > 1" class="btn btn-link btn-block text-secondary mt-1" @click="choosePlan = true">
                  Choose a different plan
                </button>
              </template>
              <template v-else>
                <div class="font-weight-bold text-dark mb-2">Choose a plan</div>
                <div class="list-group mb-3">
                  <button
                    v-for="p in packages"
                    :key="p.id"
                    type="button"
                    class="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                    :class="{ active: selectedPackage && selectedPackage.id === p.id }"
                    @click="selectedPackage = p"
                  >
                    <span class="text-left">
                      <span class="d-block font-weight-bold">{{ p.name }}</span>
                      <small :class="selectedPackage && selectedPackage.id === p.id ? 'text-white-50' : 'text-secondary'">
                        {{ durationLabel(p.duration_value, p.duration_unit) }}
                      </small>
                    </span>
                    <span class="font-weight-bold">{{ money(p.price) }}</span>
                  </button>
                </div>
                <member-pay-panel
                  v-if="selectedPackage"
                  :key="selectedPackage.id"
                  :member-token="token"
                  :intent="{ type: 'subscribe', package_id: selectedPackage.id }"
                  :amount="selectedPackage.price"
                  :wallet="member.wallet || 0"
                  :can-pay-online="canPayOnline"
                  :default-phone="member.phone || ''"
                  @done="afterPayment"
                />
                <button v-if="sub && choosePlan" class="btn btn-link btn-block text-secondary" @click="choosePlan = false">Back</button>
              </template>
            </div>
          </div>

          <!-- Wallet + visits -->
          <div class="card border-0 shadow-sm mb-3">
            <div class="card-body p-4">
              <div class="row text-center mb-3">
                <div class="col-4">
                  <div class="text-secondary small">Wallet</div>
                  <div class="font-weight-bold text-dark">{{ money(member.wallet) }}</div>
                </div>
                <div class="col-4">
                  <div class="text-secondary small">Visits</div>
                  <div class="font-weight-bold text-dark">{{ visits.length }}{{ visits.length >= 15 ? '+' : '' }}</div>
                </div>
                <div class="col-4">
                  <div class="text-secondary small">Last visit</div>
                  <div class="font-weight-bold text-dark">{{ member.last_visit ? relative(member.last_visit) : '—' }}</div>
                </div>
              </div>

              <div class="font-weight-bold text-dark mb-2">Recent visits</div>
              <ul v-if="visits.length" class="list-group list-group-flush">
                <li v-for="v in visits" :key="v.id" class="list-group-item px-0 d-flex justify-content-between">
                  <span class="text-dark">{{ formatDateTime(v.entered_at) }}</span>
                  <span class="text-secondary small">
                    <template v-if="v.exited_at">{{ duration(v.entered_at, v.exited_at) }}</template>
                    <template v-else>—</template>
                  </span>
                </li>
              </ul>
              <p v-else class="text-secondary small mb-0">No visits yet.</p>
            </div>
          </div>

          <a v-if="company.phone" :href="'tel:' + company.phone" class="btn btn-outline-secondary btn-block">
            <i class="bi bi-telephone mr-1"></i>Call {{ company.name }}
          </a>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import MemberPayPanel from './MemberPayPanel.vue'
import { durationLabel, money } from './memberStore'

/** The member's own installable "My Membership" page: pass QR, renew / join, wallet and visits. */
export default {
  name: 'MemberPortal',

  components: { MemberPayPanel },

  props: {
    token: { type: String, required: true },
  },

  data() {
    return {
      loading:         true,
      fatalError:      null,
      company:         {},
      member:          {},
      visits:          [],
      packages:        [],
      canPayOnline:    false,
      passSvg:         '',
      selectedPackage: null,
      choosePlan:      false,
      paidMessage:     '',

      installEvent:    null,
      installDismissed: false,
      isStandalone:    false,
      isIos:           false,
    }
  },

  computed: {
    sub() { return this.member.subscription },
    subActive() {
      return this.sub && this.sub.status === 'active' && this.sub.days_left !== null && this.sub.days_left >= 0
    },
    showInstall() {
      if (this.isStandalone || this.installDismissed) return false
      return !!this.installEvent || this.isIos || new URLSearchParams(window.location.search).get('install') === '1'
    },
  },

  created() {
    this.isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true
    this.isIos        = /iphone|ipad|ipod/i.test(window.navigator.userAgent)
    try { this.installDismissed = localStorage.getItem('dk_member_install_dismissed') === '1' } catch (e) { /* ignore */ }
    if (new URLSearchParams(window.location.search).get('install') === '1') this.installDismissed = false

    window.addEventListener('beforeinstallprompt', this.onBeforeInstall)
  },

  beforeDestroy() {
    window.removeEventListener('beforeinstallprompt', this.onBeforeInstall)
  },

  mounted() {
    this.load()
  },

  methods: {
    durationLabel,
    money,

    async load() {
      try {
        const { data } = await axios.get(`/api/member/${this.token}`)
        this.company      = data.company || {}
        this.member       = data.member || {}
        this.visits       = data.visits || []
        this.packages     = data.packages || []
        this.canPayOnline = data.can_pay_online
        this.passSvg      = data.pass_svg

        const wanted = new URLSearchParams(window.location.search).get('package')
        if (wanted) {
          this.selectedPackage = this.packages.find(p => p.id === wanted) || null
          if (this.selectedPackage) this.choosePlan = true
        }
      } catch (e) {
        this.fatalError = e.response?.data?.message || 'This membership link is no longer valid.'
      } finally {
        this.loading = false
      }
    },

    afterPayment(result) {
      this.paidMessage = result
        ? 'Payment received - your membership is active.'
        : 'Payment received and added to your wallet.'
      this.choosePlan      = false
      this.selectedPackage = null
      this.load()
      setTimeout(() => { this.paidMessage = '' }, 6000)
    },

    onBeforeInstall(e) {
      e.preventDefault()
      this.installEvent = e
    },

    async install() {
      if (!this.installEvent) return
      this.installEvent.prompt()
      await this.installEvent.userChoice.catch(() => null)
      this.installEvent = null
    },

    dismissInstall() {
      this.installDismissed = true
      try { localStorage.setItem('dk_member_install_dismissed', '1') } catch (e) { /* ignore */ }
    },

    formatDate(d)     { return d ? moment(d).format('D MMM YYYY') : '—' },
    formatDateTime(d) { return d ? moment(d).format('ddd D MMM, HH:mm') : '—' },
    relative(d)       { return moment(d).fromNow() },
    duration(a, b) {
      const mins = moment(b).diff(moment(a), 'minutes')
      return mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins}m`
    },
  },
}
</script>
